import React, { useState, useEffect } from 'react';
import { FaChevronRight, FaChevronLeft } from 'react-icons/fa';
import './CalendarView.scss';

const persianMonths = [
  '???????',
  '????????',
  '?????',
  '???',
  '?????',
  '??????',
  '???',
  '????',
  '???',
  '??',
  '????',
  '?????'
];

const persianDays = ['?', '?', '?', '?', '?', '?', '?'];

const CalendarView = ({ selectedDate, onDateSelect, blockedDates = [], availableDates = [], reservations = [] }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarDays, setCalendarDays] = useState([]);

  const toPersianDate = (date) => {
    const gregorianDate = new Date(date);
    const year = gregorianDate.getFullYear();
    const month = gregorianDate.getMonth();
    const day = gregorianDate.getDate();

    const persianYear = year - 621;
    const persianMonth = month < 2 ? month + 10 : month - 2;
    const persianDay = day;

    return {
      year: persianYear,
      month: persianMonth,
      day: persianDay,
      monthName: persianMonths[persianMonth],
      dayName: persianDays[gregorianDate.getDay()]
    };
  };

  useEffect(() => {
    generateCalendar();
  }, [currentMonth, blockedDates, availableDates, reservations]);

  const generateCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    const prevMonth = new Date(year, month, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthDays - i);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: false,
        isSelected: false,
        status: getDateStatus(date)
      });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const today = new Date();
      const isToday = date.toDateString() === today.toDateString();
      const isSelected = selectedDate && date.toDateString() === new Date(selectedDate).toDateString();

      days.push({
        date,
        day,
        isCurrentMonth: true,
        isToday,
        isSelected,
        status: getDateStatus(date)
      });
    }

    const totalCells = 42;
    const remainingDays = totalCells - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: false,
        isSelected: false,
        status: getDateStatus(date)
      });
    }

    setCalendarDays(days);
  };

  const getDateStatus = (date) => {
    const dateString = date.toISOString().split('T')[0];

    if (
      blockedDates.some((bd) => {
        const blockedDate = new Date(bd).toISOString().split('T')[0];
        return blockedDate === dateString;
      })
    ) {
      return 'blocked';
    }

    const dayReservations = reservations.filter((r) => {
      const resDate = new Date(r.date).toISOString().split('T')[0];
      return resDate === dateString;
    });

    if (dayReservations.length > 0) {
      return 'has-reservations';
    }

    if (
      availableDates.some((ad) => {
        const availDate = new Date(ad).toISOString().split('T')[0];
        return availDate === dateString;
      })
    ) {
      return 'available';
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) {
      return 'past';
    }

    return 'default';
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDateClick = (day) => {
    if (day.status === 'blocked' || day.status === 'past') return;
    if (onDateSelect) {
      onDateSelect(day.date.toISOString().split('T')[0]);
    }
  };

  const persianDate = toPersianDate(currentMonth);

  return (
    <div className="calendar-view">
      <div className="calendar-header">
        <button className="calendar-nav-btn" onClick={handlePrevMonth}>
          <FaChevronRight />
        </button>
        <h3 className="calendar-month-year">
          {persianDate.monthName} {persianDate.year}
        </h3>
        <button className="calendar-nav-btn" onClick={handleNextMonth}>
          <FaChevronLeft />
        </button>
      </div>

      <div className="calendar-weekdays">
        {persianDays.map((day, index) => (
          <div key={index} className="weekday">
            {day}
          </div>
        ))}
      </div>

      <div className="calendar-days">
        {calendarDays.map((day, index) => (
          <div
            key={index}
            className={`calendar-day ${day.isCurrentMonth ? 'current-month' : 'other-month'} ${day.status} ${day.isToday ? 'today' : ''} ${day.isSelected ? 'selected' : ''}`}
            onClick={() => handleDateClick(day)}
          >
            {day.day && <span className="day-number">{day.day}</span>}
            {day.status === 'has-reservations' && <span className="reservation-indicator"></span>}
          </div>
        ))}
      </div>

      <div className="calendar-legend">
        <div className="legend-item">
          <span className="legend-color available"></span>
          <span>???? ????</span>
        </div>
        <div className="legend-item">
          <span className="legend-color has-reservations"></span>
          <span>???? ????</span>
        </div>
        <div className="legend-item">
          <span className="legend-color blocked"></span>
          <span>?????</span>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;
